import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiMenu, FiX, FiCalendar } = FiIcons;

const TayyabLogo = () => (
  <div className="flex items-center space-x-2.5">
    {/* Icon cluster */}
    <div className="relative flex items-center justify-center w-10 h-10">
      <svg width="42" height="42" viewBox="0 0 42 42" fill="none" xmlns="http://www.w3.org/2000/svg">
        {/* Plug icon top-left */}
        <path d="M7 5v5M11 5v5" stroke="#38bdf8" strokeWidth="1.8" strokeLinecap="round"/>
        <rect x="5" y="10" width="8" height="5" rx="1.5" stroke="#38bdf8" strokeWidth="1.8"/>
        <path d="M9 15v3" stroke="#38bdf8" strokeWidth="1.8" strokeLinecap="round"/>
        <circle cx="9" cy="20" r="1.5" fill="#facc15"/>

        {/* Heartbeat / waveform — center */}
        <polyline
          points="13,21 16,21 18,15 20,27 22,18 24,24 26,21 29,21"
          stroke="#38bdf8" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
        />

        {/* CCTV camera top-right */}
        <rect x="28" y="9" width="10" height="7" rx="1.5" stroke="#38bdf8" strokeWidth="1.8"/>
        <polygon points="38,10.5 41,8.5 41,17.5 38,15.5" fill="#38bdf8"/>
        <circle cx="32" cy="12.5" r="1.5" fill="#facc15"/>
      </svg>
    </div>

    {/* Text */}
    <div className="flex flex-col leading-none">
      <span
        className="text-white font-black tracking-[0.12em] text-lg"
        style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}
      >
        TAYYAB
      </span>
      <span className="text-blue-400 text-[9px] font-bold tracking-[0.28em] uppercase mt-0.5">
        Electric Studio
      </span>
      <span className="text-gray-500 text-[7px] tracking-[0.15em] mt-0.5 hidden sm:block">
        POWERING HOMES, SECURING FUTURES.
      </span>
    </div>
  </div>
);

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { path: '/', label: 'Home' },
    { path: '/about', label: 'About' },
    { path: '/services', label: 'Services' },
    { path: '/contact', label: 'Contact' }
  ];

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-black/90 backdrop-blur-md border-b border-blue-900/30'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">

          {/* Logo */}
          <Link to="/" className="flex items-center">
            <TayyabLogo />
          </Link>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center space-x-8">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`text-sm font-medium transition-colors duration-300 hover:text-blue-400 ${
                  location.pathname === item.path ? 'text-blue-400' : 'text-gray-300'
                }`}
              >
                {item.label}
              </Link>
            ))}
            <Link
              to="/appointments"
              className="flex items-center space-x-2 bg-gradient-to-r from-blue-600 to-cyan-600 px-4 py-2 rounded-full text-white font-medium hover:from-blue-700 hover:to-cyan-700 transition-all duration-300 glow-blue"
            >
              <SafeIcon icon={FiCalendar} className="text-sm" />
              <span>Book Appointment</span>
            </Link>
          </div>

          {/* Mobile menu button */}
          <button onClick={() => setIsOpen(!isOpen)} className="md:hidden text-white p-2">
            <SafeIcon icon={isOpen ? FiX : FiMenu} className="text-2xl" />
          </button>
        </div>

        {/* Mobile menu */}
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="md:hidden bg-black/95 backdrop-blur-md rounded-lg mt-4 p-4 border border-blue-900/30"
          >
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setIsOpen(false)}
                className={`block py-3 px-4 text-sm font-medium transition-colors duration-300 hover:text-blue-400 ${
                  location.pathname === item.path ? 'text-blue-400' : 'text-gray-300'
                }`}
              >
                {item.label}
              </Link>
            ))}
            <Link
              to="/appointments"
              onClick={() => setIsOpen(false)}
              className="flex items-center space-x-2 bg-gradient-to-r from-blue-600 to-cyan-600 px-4 py-3 rounded-full text-white font-medium mt-4"
            >
              <SafeIcon icon={FiCalendar} className="text-sm" />
              <span>Book Appointment</span>
            </Link>
          </motion.div>
        )}
      </div>
    </motion.nav>
  );
};

export default Navbar;